import { Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { collection, collectionData, doc, docData, DocumentReference, Firestore, orderBy, query, where } from '@angular/fire/firestore';
import { combineLatest, Observable, of } from 'rxjs';
import { map, pluck, switchMap } from 'rxjs/operators';

import { TeamWithId } from '@innersight-os/shared/firestore-interfaces';
import { UsersService } from '../users/users.service';
import { DocumentData } from '@firebase/firestore-types';

@Injectable({
  providedIn: 'root'
})
export class TeamsService {
  allTeams$ = collectionData(
    query<TeamWithId, DocumentData>(
      // eslint-able-next-line @typescript-eslint/no-explicit-any
      collection(this.db, 'teams') as any,
      orderBy('name', 'asc'),
    ),
    {idField: 'id'},
  )

  myTeams$ = authState(this.auth).pipe(
    pluck('uid'),
    switchMap(uid =>
      collectionData(
        query<TeamWithId, DocumentData>(
          // eslint-able-next-line @typescript-eslint/no-explicit-any
          collection(this.db, 'teams') as any,
          where('members', 'array-contains', uid),
          orderBy('name', 'asc'),
        ),
        {idField: 'id'},
      )
    ),
  )

  myManagedTeams$ = authState(this.auth).pipe(
    pluck('uid'),
    switchMap(uid =>
      collectionData(
        query<TeamWithId, DocumentData>(
          // eslint-able-next-line @typescript-eslint/no-explicit-any
          collection(this.db, 'teams') as any,
          where('managers', 'array-contains', uid),
          orderBy('name', 'asc')
        ),
        {idField: 'id'},
      )
    ),
  )

  myVisibleTeams$: Observable<TeamWithId[]> = this.users.myRoles$.pipe(
    switchMap(roles => {
      if(roles?.admin) return this.allTeams$
      if(roles?.manager) return combineLatest([this.myTeams$, this.myManagedTeams$]).pipe(
        map(([joined, managed]) => {
          const managedIds = managed.map(t => t.id)
          const joinedNotManaged = joined.filter(t => !managedIds.includes(t.id))
          return [...managed, ...joinedNotManaged]
        })
      )
      if(roles?.segmenter) return this.myTeams$
      return of([])
    }),
    map(teams => teams.sort((a, b) => a.name.localeCompare(b.name, undefined, {sensitivity: 'base'})))
  )

  getTeam = (id: string): Observable<TeamWithId> => {
    return docData<TeamWithId>(
      doc(this.db, `teams/${id}`) as DocumentReference<TeamWithId>,
      {idField: 'id'},
    )
  }

  constructor(
    private auth: Auth,
    private db: Firestore,
    private users: UsersService,
  ) {}
}
